// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { AlertCircle, Check, FileText, TriangleAlert } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  type PrepareResponse,
  usePrepareImportMutation,
  useStartImportSessionMutation,
  useSubmitImportMutation,
  useTransformationsQuery,
  useUploadImportFileMutation,
  useValidateImportMutation,
  type ValidateResponse,
} from "@/api/hooks/use-imports";
import { type RunningTaskProgress, useTaskProgress } from "@/api/hooks/use-progress";
import { useStoresQuery } from "@/api/hooks/use-stores";
import { ImportPlusIcon } from "@/components/icons";
import { FileDropZone } from "@/components/shared/file-drop-zone";
import { InlineSpinner } from "@/components/shared/inline-spinner";
import { KeyChip } from "@/components/shared/key-chip";
import { ProgressBar } from "@/components/shared/progress-bar";
import { VirtualSelectPopover } from "@/components/shared/virtual-select-popover";
import { WizardPane } from "@/components/shared/wizard-pane";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useBackofficeContext } from "@/hooks/use-backoffice-context";
import { cn } from "@/lib/utils";

/**
 * Minimal transformation shape the dialog needs, satisfied by both the list-row
 * `EtlProcessing` and the detail response `EtlProcessingDetail`.
 */
export interface ImportDataDialogTransformation {
  etlProcessingId: number;
  shortName: string;
  description: string;
  isActive: boolean;
  isAvailablePerStore: boolean;
  chainId?: number;
  storeId?: number;
  inputFiles: Array<{
    targetName: string;
    description: string;
    fileType: string;
    exampleFileName: string;
  }>;
}

interface ImportDataDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  chainName?: string;
  storeName?: string;
  /** Pre-select a transformation (skips the Transformation step). */
  transformation?: ImportDataDialogTransformation;
}

type WizardStep = "select" | "scope" | "upload" | "validate" | "preview";

/**
 * Waiting state of a long-running wizard call. The bar only appears once the back end has actually
 * reported a counter — until then (and for calls that report none) the spinner stands alone, so a
 * silent task never shows a bar frozen at zero.
 */
function StepProgress({ label, progress }: { label: string; progress: RunningTaskProgress | null }) {
  if (!progress) return <InlineSpinner label={label} />;
  return (
    <div className="flex flex-col items-center gap-2 py-8">
      <InlineSpinner label={label} className="py-0" />
      <ProgressBar value={progress.fraction} className="max-w-[16rem]" />
    </div>
  );
}

export function ImportDataDialog({ open, onOpenChange, chainName, storeName, transformation }: ImportDataDialogProps) {
  const { t } = useTranslation("back-office");
  const needsSelectStep = transformation == null;
  const needsScopeStep = !chainName && !storeName;
  // Order of the first two steps depends on whether a transformation was supplied:
  // - Old flow (no transformation prop): user picks scope first, then transformation.
  // - New flow (transformation pre-set): select is skipped; scope step is filtered to the
  //   chains/stores applicable to that transformation.
  const STEPS = useMemo<{ key: WizardStep; label: string }[]>(() => {
    const out: { key: WizardStep; label: string }[] = [];
    if (needsScopeStep) out.push({ key: "scope", label: t("imports.wizard.steps.scope") });
    if (needsSelectStep) out.push({ key: "select", label: t("imports.wizard.steps.transformation") });
    out.push(
      { key: "upload", label: t("imports.wizard.steps.upload") },
      { key: "validate", label: t("imports.wizard.steps.validate") },
      { key: "preview", label: t("imports.wizard.steps.preview") },
    );
    return out;
  }, [needsSelectStep, needsScopeStep, t]);

  const firstStep: WizardStep = needsScopeStep ? "scope" : needsSelectStep ? "select" : "upload";
  const [step, setStep] = useState<WizardStep>(firstStep);
  const [selectedChainName, setSelectedChainName] = useState<string>("");
  const [selectedStoreName, setSelectedStoreName] = useState<string>("");
  const [selectedEtl, setSelectedEtl] = useState<ImportDataDialogTransformation | null>(transformation ?? null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [uploadedFiles, setUploadedFiles] = useState<Record<string, { name: string; size: number }>>({});
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});
  const [validateResult, setValidateResult] = useState<ValidateResponse | null>(null);
  const [prepareResult, setPrepareResult] = useState<PrepareResponse | null>(null);

  const effectiveChainName = chainName ?? selectedChainName ?? undefined;
  const effectiveStoreName = storeName ?? selectedStoreName ?? undefined;
  // Once a store is chosen, it already implies its chain — don't redundantly send the chain to
  // scoped API calls or repeat it alongside the store in the header.
  const scopedChainName = effectiveStoreName ? undefined : effectiveChainName;
  const scopedStoreName = effectiveStoreName;

  const { data: ctx, isFetching: ctxFetching } = useBackofficeContext();
  const { data: chainStoresData, isFetching: chainStoresFetching } = useStoresQuery({
    chainName: effectiveChainName || undefined,
    limit: -1,
    onlyActive: false,
    enabled: open && !!effectiveChainName,
  });
  const { data: etlData, isFetching: etlFetching } = useTransformationsQuery({
    chainName: scopedChainName || undefined,
    storeName: scopedStoreName || undefined,
    onlyActive: true,
    // Fetch the transformations list whenever the Transformation step is shown, regardless of
    // scope state. With the new ordering, transformation selection happens before scope.
    enabled: open && needsSelectStep,
  });

  const startSession = useStartImportSessionMutation();
  const uploadFile = useUploadImportFileMutation();
  const validateImport = useValidateImportMutation();
  const prepareImport = usePrepareImportMutation();
  const submitImport = useSubmitImportMutation();

  // Prepare and submit both publish a counter under the import session id while they run — prepare
  // counts the stores it transforms, submit the import requests it creates.
  const prepareProgress = useTaskProgress({
    namespace: "imports",
    taskId: sessionId,
    active: prepareImport.isPending,
  });
  const submitProgress = useTaskProgress({
    namespace: "imports",
    taskId: sessionId,
    active: submitImport.isPending,
    // Submit walks the prepared files it was not asked to ignore, and this dialog ignores none.
    expectedTotal: prepareResult?.summary.length,
  });

  useEffect(
    function resetWhenClosed() {
      if (open) return;
      setStep(firstStep);
      setSelectedChainName("");
      setSelectedStoreName("");
      setSelectedEtl(transformation ?? null);
      setSessionId(null);
      setUploadedFiles({});
      setUploadProgress({});
      setValidateResult(null);
      setPrepareResult(null);
    },
    [open, firstStep, transformation],
  );

  // Sync selectedEtl whenever a transformation prop arrives (e.g. user clicks Import data on a
  // row — the dialog was mounted with transformation=undefined and only now receives one).
  useEffect(
    function syncTransformationProp() {
      if (transformation) setSelectedEtl(transformation);
    },
    [transformation],
  );

  function handleClose() {
    onOpenChange(false);
  }

  function handleScopeNext() {
    if (needsSelectStep) setStep("select");
    else handleStartSession();
  }

  async function handleStartSession() {
    if (!selectedEtl) return;
    setStep("upload");
    try {
      const result = await startSession.mutateAsync({
        transformationId: selectedEtl.etlProcessingId,
        chainName: scopedChainName,
        storeName: scopedStoreName,
        allowInactive: !selectedEtl.isActive,
      });
      setSessionId(result.importSessionId);
    } catch {
      // Fall back to whichever step the user just came from.
      setStep(needsSelectStep ? "select" : needsScopeStep ? "scope" : "upload");
    }
  }

  async function handleFileUpload(targetName: string, file: File) {
    if (!sessionId) return;
    setUploadProgress((p) => ({ ...p, [targetName]: 0 }));
    try {
      await uploadFile.mutateAsync({
        importSessionId: sessionId,
        targetName,
        file,
        onProgress: (progress) => setUploadProgress((p) => ({ ...p, [targetName]: progress })),
      });
      setUploadedFiles((prev) => ({
        ...prev,
        [targetName]: { name: file.name, size: file.size },
      }));
    } catch {
      // Error toast handled by API middleware
    } finally {
      setUploadProgress((p) => {
        const next = { ...p };
        delete next[targetName];
        return next;
      });
    }
  }

  async function handleValidate() {
    if (!sessionId) return;
    setValidateResult(null);
    setPrepareResult(null);
    setStep("validate");
    try {
      const result = await validateImport.mutateAsync(sessionId);
      setValidateResult(result);
      if (result.status === "ok") {
        setStep("preview");
        const prepResult = await prepareImport.mutateAsync(sessionId);
        setPrepareResult(prepResult);
      }
    } catch {
      // Error toast handled by API middleware
    }
  }

  async function onSubmit() {
    if (!sessionId) return;
    try {
      await submitImport.mutateAsync({ importSessionId: sessionId });
      toast.success(t("imports.wizard.submittedSuccess"));
      handleClose();
    } catch {
      // Error toast handled by API middleware
    }
  }

  const allFilesUploaded = selectedEtl?.inputFiles.every((f) => uploadedFiles[f.targetName]) ?? false;

  // Once we've moved past Upload, any in-flight API call (validate, prepare, submit) locks the
  // dialog: no Back, no Cancel, no Esc, no X — to avoid stranding the server mid-operation.
  const isPostUploadBusy = validateImport.isPending || prepareImport.isPending || submitImport.isPending;

  const storeCodeToName = useMemo(() => {
    const map: Record<string, string> = {};
    for (const s of prepareResult?.stores ?? []) {
      if (s.storeCode) map[s.storeCode] = s.legibleName;
    }
    return map;
  }, [prepareResult?.stores]);

  const filesByStore = useMemo(() => {
    if (!prepareResult) return [];
    const groups = new Map<string, typeof prepareResult.summary>();
    for (const s of prepareResult.summary) {
      const list = groups.get(s.storeCode) ?? [];
      list.push(s);
      groups.set(s.storeCode, list);
    }
    return Array.from(groups.entries());
  }, [prepareResult]);

  const isLoading =
    startSession.isPending ||
    uploadFile.isPending ||
    validateImport.isPending ||
    prepareImport.isPending ||
    submitImport.isPending;

  const currentStepIndex = STEPS.findIndex((s) => s.key === step);

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o && isPostUploadBusy) return;
        onOpenChange(o);
      }}
    >
      <DialogContent
        id="dialog-import-data"
        className="sm:w-fit sm:min-w-[42rem] sm:max-w-[60rem] p-0 gap-0 overflow-hidden flex flex-col"
        aria-describedby={undefined}
        showCloseButton={!isPostUploadBusy}
      >
        <div className="flex flex-row max-h-[calc(85vh-3.5rem)]">
          <WizardPane icon={ImportPlusIcon} />
          <div className="flex-1 px-6 pt-6 flex flex-col gap-4 min-w-0 min-h-0">
            <DialogHeader>
              <DialogTitle>{t("imports.wizard.title")}</DialogTitle>
              {(step !== "scope" || (transformation != null && selectedEtl)) &&
                (scopedChainName || scopedStoreName || selectedEtl) && (
                  <div className="flex items-center gap-2 pt-1">
                    {scopedChainName && <KeyChip label={scopedChainName} variant="chain" />}
                    {scopedStoreName && <KeyChip label={scopedStoreName} variant="store" />}
                    {selectedEtl && <KeyChip label={selectedEtl.shortName} />}
                  </div>
                )}
            </DialogHeader>

            <div className="inline-flex self-start text-xs">
              {STEPS.map((s, i) => {
                const isPast = i < currentStepIndex;
                const isCurrent = i === currentStepIndex;
                const isAfterCurrent = i === currentStepIndex + 1;
                const isFirst = i === 0;
                const isLast = i === STEPS.length - 1;
                return (
                  <div
                    key={s.key}
                    className={cn(
                      "relative flex items-center justify-center gap-1.5 border-y border-l pl-2 pr-3 py-1.5",
                      isLast && "border-r",
                      isFirst && "rounded-l-md",
                      isLast && "rounded-r-md",
                      isCurrent && "z-10 bg-primary font-medium text-primary-foreground border-primary-edge",
                      isAfterCurrent && "border-l-primary-edge",
                      isPast && "bg-muted text-muted-foreground",
                      !isPast && !isCurrent && "text-muted-foreground",
                    )}
                  >
                    <span className="inline-flex h-4 w-4 shrink-0 items-center justify-center text-[10px] font-medium tabular-nums">
                      {isPast ? <Check className="h-3.5 w-3.5" /> : i + 1}
                    </span>
                    <span>{s.label}</span>
                  </div>
                );
              })}
            </div>

            <div className="-mx-6 flex-1 min-h-0 overflow-y-auto px-6 pt-1 pb-6 space-y-4">
              {selectedEtl && !selectedEtl.isActive && (
                <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-200">
                  <TriangleAlert className="h-4 w-4 shrink-0 mt-0.5" />
                  <div className="space-y-0.5">
                    <p className="font-medium">{t("imports.wizard.inactiveTransformation.title")}</p>
                    <p className="text-xs">{t("imports.wizard.inactiveTransformation.hint")}</p>
                  </div>
                </div>
              )}
              {step === "scope" && (
                <div className="grid grid-cols-2 gap-3">
                  <VirtualSelectPopover
                    value={selectedChainName}
                    onChange={(v) => {
                      setSelectedChainName(v);
                      setSelectedStoreName("");
                    }}
                    options={(ctx?.chains ?? [])
                      // New flow: when a transformation is supplied via props and locked to a
                      // specific chain, restrict the picker to that one. Old flow shows every chain.
                      .filter(
                        (c) => transformation == null || selectedEtl?.chainId == null || c.id === selectedEtl.chainId,
                      )
                      .map((c) => ({ label: c.display, value: c.value }))}
                    placeholder={t("imports.wizard.scope.selectChain")}
                    searchPlaceholder={t("imports.wizard.scope.searchChains")}
                    emptyText={t("imports.wizard.scope.noChains")}
                    loading={ctxFetching && !ctx}
                  />
                  {selectedChainName &&
                    // New-flow transformations that don't support per-store imports hide the
                    // store picker entirely. Old flow keeps it optional as before.
                    (transformation == null || selectedEtl?.isAvailablePerStore !== false) && (
                      <VirtualSelectPopover
                        value={selectedStoreName}
                        onChange={setSelectedStoreName}
                        options={(chainStoresData?.stores ?? [])
                          // New flow: lock to the transformation's store when it's set.
                          .filter(
                            (s) =>
                              transformation == null ||
                              selectedEtl?.storeId == null ||
                              s.storeId === selectedEtl.storeId,
                          )
                          .map((s) => ({ label: s.legibleName, value: s.storeName }))}
                        placeholder={t("imports.wizard.scope.selectStore")}
                        searchPlaceholder={t("imports.wizard.scope.searchStores")}
                        emptyText={t("imports.wizard.scope.noStores")}
                        loading={chainStoresFetching}
                      />
                    )}
                </div>
              )}

              {step === "select" && (
                <div className="space-y-4">
                  <div className="w-1/2">
                    <VirtualSelectPopover
                      value={selectedEtl?.etlProcessingId?.toString() ?? ""}
                      onChange={(val) => {
                        const etl = etlData?.etlProcessings.find((e) => e.etlProcessingId.toString() === val);
                        setSelectedEtl(etl ?? null);
                      }}
                      options={(etlData?.etlProcessings ?? []).map((etl) => ({
                        label: etl.shortName,
                        value: etl.etlProcessingId.toString(),
                      }))}
                      placeholder={t("imports.wizard.transformation.select")}
                      searchPlaceholder={t("imports.wizard.transformation.search")}
                      emptyText={t("imports.wizard.transformation.empty")}
                      loading={etlFetching}
                    />
                  </div>

                  {selectedEtl && (
                    <div className="space-y-3">
                      {selectedEtl.description && <p className="text-sm">{selectedEtl.description}</p>}
                      <div>
                        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-2">
                          {t("imports.wizard.transformation.filesToPrepare")}
                        </p>
                        <ul className="divide-y rounded-md border">
                          {selectedEtl.inputFiles.map((f) => (
                            <li key={f.targetName} className="flex items-center gap-2 px-3 py-2">
                              <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-medium truncate">{f.description}</p>
                                <p className="text-xs text-muted-foreground truncate">
                                  e.g. {f.exampleFileName} ({f.fileType})
                                </p>
                              </div>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {step === "upload" && selectedEtl && (
                <div className="space-y-4">
                  {!sessionId ? (
                    <InlineSpinner label={t("imports.wizard.upload.startingSession")} />
                  ) : (
                    selectedEtl.inputFiles.map((inputFile) => (
                      <FileDropZone
                        key={inputFile.targetName}
                        accept=".csv,.xlsx,.parquet"
                        selected={uploadedFiles[inputFile.targetName]}
                        progress={uploadProgress[inputFile.targetName]}
                        title={inputFile.description}
                        subtitle={t("imports.wizard.upload.dropFile", { fileType: inputFile.fileType })}
                        uploadingLabel={(percent) => t("imports.wizard.upload.uploadingPercent", { percent })}
                        processingLabel={t("imports.wizard.upload.processing")}
                        onSelect={(file) => handleFileUpload(inputFile.targetName, file)}
                      />
                    ))
                  )}
                </div>
              )}

              {step === "validate" && (
                <div className="space-y-4">
                  {validateImport.isPending || !validateResult ? (
                    <InlineSpinner label={t("imports.wizard.validate.validating")} />
                  ) : (
                    <div className="rounded-lg border border-destructive bg-destructive/10 p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <AlertCircle className="h-5 w-5 text-destructive" />
                        <span className="font-medium text-destructive">{t("imports.wizard.validate.errorsTitle")}</span>
                      </div>
                      <p className="text-sm text-muted-foreground mb-3">
                        {t("imports.wizard.validate.errorsCount", {
                          errors: validateResult.metrics.errorsFound,
                          files: validateResult.metrics.processedFiles,
                        })}
                      </p>
                      {validateResult.summary.map((s, i) => (
                        <div key={i} className="mb-2">
                          <p className="text-sm font-medium">{s.fileType}</p>
                          {s.errors.map((err, j) => (
                            <p key={j} className="text-sm text-muted-foreground pl-4">
                              {err.description}
                              {err.columnName &&
                                ` ${t("imports.wizard.validate.errorColumn", { column: err.columnName })}`}
                              {err.location && ` ${t("imports.wizard.validate.errorRow", { location: err.location })}`}
                            </p>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {step === "preview" && (
                <div className="space-y-4">
                  {submitImport.isPending ? (
                    <StepProgress
                      progress={submitProgress}
                      label={
                        submitProgress
                          ? t("imports.wizard.preview.submittingProgress", {
                              current: submitProgress.current,
                              total: submitProgress.total,
                            })
                          : t("imports.wizard.preview.submitting")
                      }
                    />
                  ) : prepareImport.isPending ? (
                    <StepProgress
                      progress={prepareProgress}
                      label={
                        prepareProgress
                          ? t("imports.wizard.preview.preparingProgress", {
                              current: prepareProgress.current,
                              total: prepareProgress.total,
                            })
                          : t("imports.wizard.preview.preparing")
                      }
                    />
                  ) : prepareResult ? (
                    <div className="space-y-5">
                      <div className="space-y-2">
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                          <span className="text-muted-foreground">{t("imports.wizard.preview.filesProcessed")}</span>
                          <span>{prepareResult.metrics.processedFiles}</span>
                          <span className="text-muted-foreground">{t("imports.wizard.preview.validFiles")}</span>
                          <span>{prepareResult.metrics.validFiles}</span>
                          <span className="text-muted-foreground">{t("imports.wizard.preview.totalRecords")}</span>
                          <span>{prepareResult.metrics.totalRecords}</span>
                          <span className="text-muted-foreground">{t("imports.wizard.preview.storesAffected")}</span>
                          <span>{prepareResult.metrics.storeCodes.length}</span>
                          <span className="text-muted-foreground">{t("imports.wizard.preview.estDuration")}</span>
                          <span>{prepareResult.metrics.estimatedImportDuration}s</span>
                        </div>
                        {prepareResult.metrics.storeCodes.length > 0 && (
                          <div className="pt-2">
                            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-2">
                              {t("imports.wizard.preview.storeCodes")}
                            </p>
                            <div className="flex flex-wrap gap-1">
                              {prepareResult.metrics.storeCodes.map((code) => (
                                <Badge key={code} variant="outline">
                                  {code}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {filesByStore.length > 0 && (
                        <div>
                          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-2">
                            {t("imports.wizard.preview.importRequests")}
                          </p>
                          <ul className="space-y-3 text-sm">
                            {filesByStore.map(([storeCode, files]) => {
                              const legibleName = storeCodeToName[storeCode];
                              return (
                                <li key={storeCode}>
                                  <div className="mb-1 flex items-baseline gap-2">
                                    <span className="font-medium">{legibleName ?? storeCode}</span>
                                    {legibleName && (
                                      <span className="font-mono text-xs text-muted-foreground">{storeCode}</span>
                                    )}
                                  </div>
                                  <ul className="divide-y rounded-md border">
                                    {files.map((s) => (
                                      <li
                                        key={s.preparedImportFileId}
                                        className="flex items-center justify-between px-3 py-2"
                                      >
                                        <span className="font-medium">{s.importType}</span>
                                        <span>{t("imports.wizard.preview.itemsCount", { count: s.itemsFound })}</span>
                                      </li>
                                    ))}
                                  </ul>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      )}
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="mx-0 mb-0 rounded-none rounded-b-lg">
          <Button type="button" variant="outline" onClick={handleClose} disabled={isPostUploadBusy}>
            {t("buttons.cancel")}
          </Button>

          {step === "scope" && (
            <Button onClick={handleScopeNext} disabled={!selectedChainName}>
              {t("buttons.next")}
            </Button>
          )}

          {step === "select" && (
            <>
              {needsScopeStep && (
                <Button variant="outline" onClick={() => setStep("scope")}>
                  {t("buttons.back")}
                </Button>
              )}
              <Button onClick={handleStartSession} disabled={!selectedEtl}>
                {t("buttons.next")}
              </Button>
            </>
          )}

          {step === "upload" && (
            <>
              <Button
                variant="outline"
                onClick={() => {
                  setSessionId(null);
                  setUploadedFiles({});
                  setValidateResult(null);
                  setPrepareResult(null);
                  // Step back to the most recent input step.
                  setStep(needsSelectStep ? "select" : needsScopeStep ? "scope" : "upload");
                }}
                disabled={startSession.isPending}
              >
                {t("buttons.back")}
              </Button>
              <Button onClick={handleValidate} disabled={!sessionId || !allFilesUploaded || isLoading}>
                {t("buttons.validate")}
              </Button>
            </>
          )}

          {(step === "validate" || step === "preview") && (
            <>
              <Button variant="outline" onClick={() => setStep("upload")} disabled={isPostUploadBusy}>
                {t("buttons.back")}
              </Button>
              <Button onClick={onSubmit} disabled={step !== "preview" || !prepareResult || isLoading}>
                <ImportPlusIcon className="h-4 w-4" />
                {t("imports.wizard.submitButton")}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
